import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChartBar, CalendarX, Warning, ListChecks, FilePdf, FileXls, Stamp, Flag,
} from '@phosphor-icons/react';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RP76-79 (AMO-A2, AMO-A3, AMO-A4) — « Point MOA » d'un projet : ce que l'AMO
 * reconstituait chaque mois à la main dans Excel.
 *   1. Budget : enveloppe → estimation (DPGF) → marchés → avenants → situations
 *      certifiées → reste à payer → prévision de fin ;
 *   2. Planning comparé au planning de référence, retards par lot ;
 *   3. Décisions en attente et risques (avenants proposés, PV à signer, visas en
 *      retard, réserves ouvertes) ;
 *   4. Export de la note de point MOA (PDF et Excel) avec le commentaire de l'AMO.
 * Accessible à l'AMO et à l'architecte (même vue pour son propre point).
 */

const fmtEur = (v) => (v === null || v === undefined
  ? '—'
  : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`);
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};
const fmtEcart = (j) => (j === null || j === undefined ? '—' : `${j > 0 ? '+' : ''}${j} j`);
const NIVEAU = { eleve: 'bg-[#FDECEA] text-[#8B3A3A]', moyen: 'bg-[#FFF8E1] text-[#8A6A0B]' };
const MISE_EN_AVANT = new Set(['montant_actuel', 'reste', 'prevision']);

function Section({ titre, icon: Icon, children, testid }) {
  return (
    <section className="border border-[#E0E0DB] bg-white" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#E0E0DB] flex items-center gap-2">
        <Icon size={18} className="text-[#C75B39]" />
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">{titre}</h3>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function PointMOA({ chantierId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refus, setRefus] = useState(null);
  const [commentaire, setCommentaire] = useState('');
  const [export_, setExport] = useState(null);

  useEffect(() => {
    let vivant = true;
    setLoading(true);
    axios.get(`${API_URL}/api/chantiers/${chantierId}/point-moa`, { withCredentials: true })
      .then(({ data: d }) => { if (vivant) setData(d); })
      .catch((err) => {
        if (vivant) setRefus(err?.response?.data?.detail || "Impossible d'afficher le point MOA pour le moment.");
      })
      .finally(() => { if (vivant) setLoading(false); });
    return () => { vivant = false; };
  }, [chantierId]);

  const exporter = async (format) => {
    setExport(format);
    try {
      const url = format === 'pdf'
        ? `${API_URL}/api/chantiers/${chantierId}/point-moa/export-pdf`
        : `${API_URL}/api/chantiers/${chantierId}/point-moa/export-xlsx`;
      const res = await axios.get(url, { params: { commentaire }, responseType: 'blob', withCredentials: true });
      const dispo = res.headers['content-disposition'] || '';
      const m = dispo.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
      const nom = m ? decodeURIComponent(m[1] || m[2]) : `Note_point_MOA.${format}`;
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = nom;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch {
      toast.error("L'export n'a pas pu être généré");
    } finally {
      setExport(null);
    }
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;
  if (refus) return <p className="text-sm text-[#6B6B6B] border border-[#E0E0DB] bg-white p-4">{refus}</p>;
  if (!data) return null;

  const b = data.budget;
  const p = data.planning;
  return (
    <div className="space-y-5 max-w-5xl" data-testid="point-moa">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <h2 className="text-lg text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>Point MOA</h2>
          <p className="text-sm text-[#6B6B6B]">
            Budget, planning, risques et décisions au {fmtDate(data.date)}{data.chantier.phase ? ` · étape : ${data.chantier.phase}` : ''}.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => exporter('pdf')} disabled={!!export_}
            className="inline-flex items-center gap-1 text-sm px-3 py-2 border border-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white disabled:opacity-50"
            data-testid="point-moa-export-pdf">
            <FilePdf size={16} /> {export_ === 'pdf' ? 'Export…' : 'Exporter en PDF'}
          </button>
          <button type="button" onClick={() => exporter('xlsx')} disabled={!!export_}
            className="inline-flex items-center gap-1 text-sm px-3 py-2 border border-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white disabled:opacity-50"
            data-testid="point-moa-export-xlsx">
            <FileXls size={16} /> {export_ === 'xlsx' ? 'Export…' : 'Exporter en Excel'}
          </button>
        </div>
      </div>

      {/* RP76 — budget en cascade */}
      <Section titre="Budget" icon={ChartBar} testid="point-moa-budget">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-[#F0F0EC]">
              {b.cascade.map(l => (
                <tr key={l.cle} className={MISE_EN_AVANT.has(l.cle) ? 'font-semibold' : ''}>
                  <td className="py-1.5 pr-2 text-[#2C2C2C]">{l.libelle}</td>
                  <td className="py-1.5 text-right whitespace-nowrap">{fmtEur(l.montant_ht)} HT</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-sm space-y-2">
            {b.ecart_enveloppe_ht !== null && (
              <p className={b.ecart_enveloppe_ht > 0 ? 'text-[#8B3A3A]' : 'text-[#2D6A4F]'}>
                Prévision de fin {b.ecart_enveloppe_ht > 0 ? 'au-dessus' : 'dans'} de l'enveloppe
                ({fmtEur(Math.abs(b.ecart_enveloppe_ht))} {b.ecart_enveloppe_ht > 0 ? 'de dépassement' : 'de marge'}).
              </p>
            )}
            {b.avenants_proposes_ht !== 0 && (
              <p className="text-[#6B6B6B]">Avenants proposés, non encore acceptés : {fmtEur(b.avenants_proposes_ht)} HT.</p>
            )}
          </div>
        </div>
        {b.par_lot.length > 0 && (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-[#F5F5F3] text-xs text-[#6B6B6B]">
                <tr>
                  <th className="text-left px-2 py-2">Lot</th>
                  <th className="text-right px-2 py-2">Marché</th>
                  <th className="text-right px-2 py-2">Certifié</th>
                  <th className="text-right px-2 py-2">Payé</th>
                  <th className="text-right px-2 py-2">Reste</th>
                  <th className="text-right px-2 py-2">Avancement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F0EC]">
                {b.par_lot.map(l => (
                  <tr key={`${l.lot}-${l.entreprise}`}>
                    <td className="px-2 py-1.5"><span className="block">{l.lot}</span><span className="text-xs text-[#6B6B6B]">{l.entreprise}</span></td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{fmtEur(l.marche_ht)}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{fmtEur(l.certifie_ht)}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{fmtEur(l.paye_ht)}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{fmtEur(l.reste_ht)}</td>
                    <td className="px-2 py-1.5 text-right">{Math.round(l.avancement_pct)} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* RP77 — planning comparé au planning de référence */}
      <Section titre="Planning" icon={CalendarX} testid="point-moa-planning">
        {!p.a_un_planning ? (
          <p className="text-sm text-[#9A9A9A]">Aucun planning n'est encore établi.</p>
        ) : (
          <>
            <p className="text-sm text-[#2C2C2C]">
              Fin prévue : <strong>{fmtDate(p.fin_prevue)}</strong>
              {p.a_une_reference ? (
                <> · fin du planning de référence{p.reference_libelle ? ` (« ${p.reference_libelle} »)` : ''} : <strong>{fmtDate(p.fin_reference)}</strong> · écart : <strong className={p.ecart_fin_jours > 0 ? 'text-[#8B3A3A]' : ''}>{fmtEcart(p.ecart_fin_jours)}</strong></>
              ) : (
                <span className="text-[#9A9A9A]"> · aucun planning de référence enregistré</span>
              )}
            </p>
            {p.par_lot.length > 0 && (
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="bg-[#F5F5F3] text-xs text-[#6B6B6B]">
                    <tr>
                      <th className="text-left px-2 py-2">Lot</th>
                      <th className="text-right px-2 py-2">Avancement</th>
                      <th className="text-right px-2 py-2">Fin prévue</th>
                      <th className="text-right px-2 py-2">Fin de référence</th>
                      <th className="text-right px-2 py-2">Écart</th>
                      <th className="text-right px-2 py-2">Tâches en retard</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0F0EC]">
                    {p.par_lot.map(l => (
                      <tr key={l.lot}>
                        <td className="px-2 py-1.5"><span className="block">{l.lot}</span><span className="text-xs text-[#6B6B6B]">{l.entreprise}</span></td>
                        <td className="px-2 py-1.5 text-right">{Math.round(l.avancement_pct)} %</td>
                        <td className="px-2 py-1.5 text-right">{fmtDate(l.fin_prevue)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtDate(l.fin_reference)}</td>
                        <td className={`px-2 py-1.5 text-right ${l.ecart_jours > 0 ? 'text-[#8B3A3A] font-semibold' : ''}`}>{fmtEcart(l.ecart_jours)}</td>
                        <td className="px-2 py-1.5 text-right">{l.taches_en_retard || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Section>

      {/* RP78 — décisions en attente et risques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section titre="Décisions en attente" icon={ListChecks} testid="point-moa-decisions">
          {data.decisions_en_attente.length === 0 ? (
            <p className="text-sm text-[#9A9A9A]">Aucune décision en attente.</p>
          ) : (
            <ul className="space-y-2">
              {data.decisions_en_attente.map((d, i) => (
                <li key={`${d.type}-${i}`} className="text-sm border-l-4 border-[#C75B39] pl-3">
                  <p className="font-semibold text-[#2C2C2C]">{d.libelle}</p>
                  <p className="text-xs text-[#6B6B6B]">
                    {[d.detail, d.montant_ht !== null && d.montant_ht !== undefined ? `${fmtEur(d.montant_ht)} HT` : null, d.depuis ? `depuis le ${fmtDate(d.depuis)}` : null].filter(Boolean).join(' · ')}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section titre="Risques" icon={Warning} testid="point-moa-risques">
          {data.risques.length === 0 ? (
            <p className="text-sm text-[#9A9A9A]">Aucun risque relevé.</p>
          ) : (
            <ul className="space-y-2">
              {data.risques.map((r, i) => (
                <li key={`${r.type}-${i}`} className="text-sm flex items-start gap-2">
                  <span className={`text-[10px] uppercase px-1.5 py-0.5 ${NIVEAU[r.niveau] || NIVEAU.moyen}`}>{r.niveau === 'eleve' ? 'Élevé' : 'Moyen'}</span>
                  <span className="text-[#2C2C2C]">{r.libelle}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {(data.visas_en_retard.length > 0 || data.reserves_par_lot.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.visas_en_retard.length > 0 && (
            <Section titre="Visas en retard" icon={Stamp} testid="point-moa-visas">
              <ul className="space-y-1 text-sm">
                {data.visas_en_retard.map((v, i) => (
                  <li key={`${v.titre}-${i}`} className="flex justify-between gap-2">
                    <span>{v.titre}<span className="text-xs text-[#6B6B6B]">{v.lot ? ` · ${v.lot}` : ''}</span></span>
                    <span className="text-[#8B3A3A] whitespace-nowrap">+{v.jours_de_retard} j</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {data.reserves_par_lot.length > 0 && (
            <Section titre="Réserves ouvertes" icon={Flag} testid="point-moa-reserves">
              <ul className="space-y-1 text-sm">
                {data.reserves_par_lot.map(l => (
                  <li key={l.lot} className="flex justify-between gap-2">
                    <span>{l.lot_nom || l.lot}</span>
                    <span className="text-[#6B6B6B] whitespace-nowrap">{l.ouvertes} ouverte(s) · {l.a_verifier} à vérifier</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}

      {/* RP79 — commentaire de l'AMO repris dans la note exportée */}
      <section className="border border-[#E0E0DB] bg-white p-4" data-testid="point-moa-commentaire">
        <label htmlFor="point-moa-commentaire" className="text-sm font-semibold text-[#2C2C2C]">Commentaire pour la note de point MOA</label>
        <p className="text-xs text-[#6B6B6B] mb-2">Repris dans l'export PDF et Excel ; il n'est pas enregistré dans le projet.</p>
        <Textarea
          id="point-moa-commentaire"
          value={commentaire}
          onChange={e => setCommentaire(e.target.value.slice(0, 4000))}
          rows={4}
          className="rounded-none"
          placeholder="Synthèse, points d'attention, décisions à prendre au prochain comité…"
        />
      </section>
    </div>
  );
}
